import React, { useState, useEffect } from "react";
import * as _ from "lodash";
import { Dispatch, connect } from "react-redux";
import { IReportState, IApplicationState, AppActionCreators, ReportActionCreators, IDonorBM3ValidatorResponse } from "@nkr/store/index";
import { IDonorBM3ValidatorList } from '@nkr/models/index';
import { HeaderWrapper } from './styles';
import { GridApi, ColumnApi, GridReadyEvent, CsvExportParams, ProcessCellForExportParams } from 'ag-grid-community';
import { FormControl, Button, Well, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import Grid from './grid';

interface IOwnProps { }

interface IStateProps {
    reports: IReportState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getBM3Validator: (donorAlias: string) => Promise<IDonorBM3ValidatorResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

const DonorBM3ValidatorReport: React.FunctionComponent<TProps> = ({ showLoading, hideLoading, getBM3Validator }) => {
    const [donorAlias, setDonorAlias] = useState<string>('');
    const [isAvoids, setIsAvoids] = useState<boolean>(false);
    const [isSuppressed, setIsSuppressed] = useState<boolean>(false);
    const [report, setReport] = useState<IDonorBM3ValidatorList[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);

    useEffect(() => {
        const donorAlias = '';
        getData(donorAlias);
    }, []);

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi);
    };

    const getData = (donorAlias: string) => {
        showLoading('Getting report data, one moment please...')
            .then(() => getBM3Validator(donorAlias))
            .then((response: IDonorBM3ValidatorResponse) => {
                setReport(response.data.results);
                hideLoading();
            })
            .catch(() => {
                alert('An error occurred while loading report data');
                hideLoading();
            });
    };

    const handleAliasChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        setDonorAlias(e.currentTarget.value);
    };

    const handleAliasSearch = () => {
        getData(donorAlias);
    };

    const csvExport = () => {
        if (gridApi) {
            const params: CsvExportParams = {
                columnGroups: true,
                fileName: 'Donor-BM3-Validator-Report.csv',
                processCellCallback: (params: ProcessCellForExportParams) => {
                    const colDef = params.column.getColDef();

                    if (colDef.headerName === 'Rank') {
                        return params.node.rowIndex + 1;
                    } else if (colDef.colId === 'matchEpletRisk') {
                        return params.value;
                    }
                    return params.value;
                }
            };

            gridApi.exportDataAsCsv(params);
        }
    };

    const getFilteredData = (): IDonorBM3ValidatorList[] => {
        return report.filter(x => {
            let isSuppress = isSuppressed ? x.inMatch : !x.inMatch;
            let isAvoid = isAvoids ? x.isAvoid : !x.isAvoid;
            return isSuppress && isAvoid;
        });
    };

    const filteredData = getFilteredData();

    return (
        <>
            <HeaderWrapper>
                <div className='billing-header'>
                    Donor BM3 Validator Report
                </div>

                <Well bsSize='sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}>Donor Alias:&nbsp;</div>
                            <div>
                                <FormControl
                                    type='text'
                                    name='donorAlias'
                                    value={donorAlias}
                                    onChange={handleAliasChange}
                                />
                            </div>
                        </div>

                        <div>
                            <Button bsStyle={'primary'} onClick={handleAliasSearch}>Search</Button>
                        </div>

                        <div style={{ display: 'flex', alignSelf: 'right', alignItems: 'right', marginLeft: 100 }}>
                            <ToggleButtonGroup type='radio' name='suppress' value={isSuppressed ? 1 : 0}>
                                <ToggleButton type='radio' value={1} className={isSuppressed ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => setIsSuppressed(true)}>Suppress</ToggleButton>
                                <ToggleButton type='radio' value={0} className={!isSuppressed ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => setIsSuppressed(false)}>Unsuppress</ToggleButton>
                            </ToggleButtonGroup>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 100 }}>
                            <input id='includeAvoids' type="checkbox" checked={isAvoids} style={{ margin: 0 }} onChange={() => setIsAvoids(prevState => !prevState)} />&nbsp;
                            <label style={{ margin: 0, fontWeight: 'normal' }} htmlFor='missingDonationConfirmation'>Include Avoids</label>
                        </div>
                    </div>


                    <div style={{ display: 'flex', marginLeft: '50px' }}>
                        <Button onClick={csvExport}>Export CSV</Button>
                    </div>
                </Well>
            </HeaderWrapper >

            <Grid data={filteredData} onGridReady={onGridReady} />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getBM3Validator: (donorAlias) => {
            return dispatch(ReportActionCreators.getBM3Validator(donorAlias));
        }
    }
};

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(DonorBM3ValidatorReport);
